import React, { useState } from "react";
import { X, Star, Loader2, Sparkles } from "lucide-react";
import { createReview } from "../../api/reviews";
import { useToast } from "../../context/ToastContext";

export default function BookingReviewModal({
  isOpen,
  onClose,
  bookingId,
  professionalId,
  professionalName = "Professional",
  professionalImage = null,
  eventName = "Your Event",
  onSuccess,
}) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [reviewMsg, setReviewMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { showSuccess, showError } = useToast();

  if (!isOpen) return null;

  const ratingLabels = ["Very Poor", "Poor", "Good", "Very Good", "Excellent"];

  const wordCount = (str) =>
    str
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (rating === 0) {
      newErrors.rating = "Please select a rating.";
    }

    if (!title.trim()) {
      newErrors.title = "Review Title is required.";
    } else if (wordCount(title) > 15) {
      newErrors.title = "Title cannot exceed 15 words.";
    }

    const words = wordCount(reviewMsg);
    if (!reviewMsg.trim()) {
      newErrors.reviewMsg = "Review message is required.";
    } else if (words < 10) {
      newErrors.reviewMsg = "Review message must be at least 10 words.";
    } else if (reviewMsg.length > 2000) {
      newErrors.reviewMsg = "Review message cannot exceed 2000 characters.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");

    if (!userId && token) {
      try {
        const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(base64));
        userId = payload.id || payload.userId || payload._id;
      } catch (err) {
        console.error("Failed to parse token payload", err);
      }
    }

    if (!userId) {
      setErrors({ global: "User session not found. Please log in again." });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await createReview({
        userId,
        professionalId,
        bookingId,
        rating,
        title: title.trim(),
        reviewMsg: reviewMsg.trim(),
      });

      showSuccess("Review submitted successfully! It will be visible once approved by an admin.");
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      const msg = err.message || "Failed to submit review.";
      setErrors({ global: msg });
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[950] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
    >
      <div className="bg-white relative w-full max-w-[500px] rounded-2xl shadow-2xl my-auto p-5 sm:p-8 max-h-[95vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-700 transition-colors z-20 cursor-pointer"
        >
          <X size={18} />
        </button>

        {/* Header Title */}
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#FF4C18]" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#FF4C18]">
            Verified Booking Review
          </span>
        </div>
        <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-2">
          Rate & Review Professional
        </h2>

        {/* Professional & Event Context Card */}
        <div className="flex items-center gap-3.5 p-3 bg-gray-50 rounded-xl border border-gray-100 mb-5">
          <div className="w-12 h-12 rounded-full bg-white border border-gray-200 shadow-xs flex items-center justify-center overflow-hidden shrink-0">
            {professionalImage ? (
              <img
                src={professionalImage}
                alt={professionalName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-sm font-bold text-gray-400">
                {professionalName?.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">
              {professionalName}
            </h3>
            {eventName && (
              <p className="text-xs text-gray-500 font-medium truncate">
                Event: <span className="text-gray-700 font-semibold">{eventName}</span>
              </p>
            )}
          </div>
        </div>

        {errors.global && (
          <div className="p-3 mb-4 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg">
            {errors.global}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          {/* Rating */}
          <div>
            <p className="text-xs sm:text-[13px] font-bold text-gray-700 mb-2">
              How would you rate your overall experience? <span className="text-red-500">*</span>
            </p>
            <div className="relative">
              <div className="flex justify-between items-center max-w-[360px] mx-auto sm:mx-0 py-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <div
                    key={star}
                    className="flex flex-col items-center gap-1 cursor-pointer group select-none"
                    onClick={() => {
                      setRating(star);
                      setErrors((prev) => ({ ...prev, rating: "" }));
                    }}
                  >
                    <Star
                      size={28}
                      className={`transition-transform duration-150 group-hover:scale-110 ${
                        rating >= star ? "fill-[#FF4C18] text-[#FF4C18]" : "text-gray-300 hover:text-orange-300"
                      }`}
                      strokeWidth={rating >= star ? 0 : 1.5}
                    />
                    <span className="text-[10px] font-bold text-gray-600 mt-0.5">
                      {ratingLabels[star - 1]}
                    </span>
                  </div>
                ))}
              </div>
              {errors.rating && (
                <p className="text-[10px] text-red-500 font-medium mt-1">
                  {errors.rating}
                </p>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs sm:text-[13px] font-bold text-gray-700">
                Review Headline <span className="text-red-500">*</span>
              </label>
              <span className="text-[10px] text-gray-400 font-medium">
                Max 15 words
              </span>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors((prev) => ({ ...prev, title: "" }));
              }}
              placeholder="e.g. Excellent service and highly professional execution"
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-[#FF4C18] text-xs sm:text-[13px] text-gray-800 placeholder-gray-400 font-medium transition"
              maxLength={120}
            />
            {errors.title && (
              <p className="text-[10px] text-red-500 font-medium mt-1">
                {errors.title}
              </p>
            )}
          </div>

          {/* Review Message */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs sm:text-[13px] font-bold text-gray-700">
                Detailed Feedback <span className="text-red-500">*</span>
              </label>
              <span className="text-[10px] text-gray-400 font-medium">
                {wordCount(reviewMsg)} words (min 10)
              </span>
            </div>
            <textarea
              rows={4}
              value={reviewMsg}
              onChange={(e) => {
                setReviewMsg(e.target.value);
                setErrors((prev) => ({ ...prev, reviewMsg: "" }));
              }}
              placeholder="Describe what you liked, punctuality, communication, quality of deliverables, etc."
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-[#FF4C18] text-xs sm:text-[13px] text-gray-800 placeholder-gray-400 font-medium resize-none transition"
              maxLength={2000}
            />
            {errors.reviewMsg && (
              <p className="text-[10px] text-red-500 font-medium mt-0.5">
                {errors.reviewMsg}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-600 font-bold text-xs sm:text-[13px] hover:bg-gray-50 transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 px-4 rounded-xl bg-[#FF4C18] text-white font-bold text-xs sm:text-[13px] hover:bg-[#e04315] transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                "Submit Review"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

